import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslatePipe} from '@ngx-translate/core';
import {MetaData} from '../../api/interfaces/metadata';

export const BOTTLENECK_KEY = 'bottleneck';
export const BOTTLENECK_REASON_KEY = 'bottleneck.reason';

export type BottleneckSeverity = 'low' | 'medium' | 'high';

const SEVERITY_COLORS: Record<BottleneckSeverity, string> = {
  low: '#fbc02d',
  medium: '#f57c00',
  high: '#d32f2f',
};

@Component({
  selector: 'maporium-bottleneck-marker',
  standalone: true,
  template: `
    @if (readonly) {
      @if (isMarked) {
        <div class="row no-gutter align-items-center mt-1">
          <div class="col-12 d-flex align-items-center">
            <span class="severity-dot" [style.background]="severityColor"></span>
            <span class="summary-text">{{ 'SHARED_MISC.BOTTLENECK.SUMMARY' | translate: { severity: severity } }}</span>
          </div>
        </div>
        @if (reason) {
          <div class="row no-gutter mt-1">
            <div class="col-12 reason-text">{{ reason }}</div>
          </div>
        }
      }
    } @else {
      <div class="row no-gutter align-items-center">
        <div class="col-10">{{ 'SHARED_MISC.BOTTLENECK.MARK_AS_BOTTLENECK' | translate }}</div>
        <div class="col-2 justify-content-end d-flex">
          <mat-slide-toggle [ngModel]="isMarked" (ngModelChange)="onToggle($event)"></mat-slide-toggle>
        </div>
      </div>
      @if (isMarked) {
        <div class="row no-gutter mt-2">
          <div class="col-12">
            <mat-button-toggle-group class="severity-group"
                                     hideSingleSelectionIndicator
                                     [value]="severity"
                                     (change)="onSeverityChange($event.value)">
              <mat-button-toggle value="low">{{ 'SHARED_MISC.BOTTLENECK.LOW' | translate }}</mat-button-toggle>
              <mat-button-toggle value="medium">{{ 'SHARED_MISC.BOTTLENECK.MEDIUM' | translate }}</mat-button-toggle>
              <mat-button-toggle value="high">{{ 'SHARED_MISC.BOTTLENECK.HIGH' | translate }}</mat-button-toggle>
            </mat-button-toggle-group>
          </div>
        </div>
        <div class="row no-gutter mt-2">
          <div class="col-12">
            <textarea class="reason-input"
                      rows="2"
                      [placeholder]="'SHARED_MISC.BOTTLENECK.REASON_PLACEHOLDER' | translate"
                      [ngModel]="reason"
                      (ngModelChange)="onReasonChange($event)"></textarea>
          </div>
        </div>
      }
    }
  `,
  styles: [`
    .severity-group {
      width: 100%;
      height: 28px;
      align-items: center;

      mat-button-toggle {
        flex: 1;
        font-size: 12px;
        height: 100%;

        ::ng-deep .mat-button-toggle-label-content {
          line-height: 26px;
        }
      }

      .mat-button-toggle-checked {
        font-weight: 600;
      }
    }

    .reason-input {
      width: 100%;
      font-size: 12px;
      border: 1px solid rgba(0, 0, 0, 0.2);
      border-radius: 4px;
      padding: 4px 6px;
      resize: vertical;
    }

    .severity-dot {
      display: inline-block;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      margin-right: 6px;
    }

    .summary-text {
      font-size: 12px;
      text-transform: capitalize;
    }

    .reason-text {
      font-size: 12px;
      opacity: 0.8;
    }
  `],
  imports: [
    CommonModule,
    FormsModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
    MatTooltipModule,
    TranslatePipe,
  ],
})
export class BottleneckMarkerComponent implements OnChanges, OnDestroy {
  @Input() metadata: MetaData[] | undefined;
  @Input() readonly = false;
  @Output() metadataChange = new EventEmitter<MetaData[]>();

  isMarked = false;
  severity: BottleneckSeverity = 'medium';
  reason = '';

  private reasonTimer?: ReturnType<typeof setTimeout>;

  ngOnChanges(): void {
    const marker = (this.metadata || []).find(m => m.key === BOTTLENECK_KEY);
    this.isMarked = !!marker;
    if (marker && ['low', 'medium', 'high'].includes(marker.value as string)) {
      this.severity = marker.value as BottleneckSeverity;
    }
    this.reason = ((this.metadata || []).find(m => m.key === BOTTLENECK_REASON_KEY)?.value as string) || '';
  }

  ngOnDestroy(): void {
    if (this.reasonTimer) {
      clearTimeout(this.reasonTimer);
      this.emitChange();
    }
  }

  get severityColor(): string {
    return SEVERITY_COLORS[this.severity];
  }

  onToggle(marked: boolean): void {
    this.isMarked = marked;
    this.flushReasonTimer();
    this.emitChange();
  }

  onSeverityChange(severity: BottleneckSeverity): void {
    this.severity = severity;
    this.flushReasonTimer();
    this.emitChange();
  }

  onReasonChange(reason: string): void {
    this.reason = reason;
    if (this.reasonTimer) {
      clearTimeout(this.reasonTimer);
    }
    this.reasonTimer = setTimeout(() => {
      this.reasonTimer = undefined;
      this.emitChange();
    }, 600);
  }

  private flushReasonTimer(): void {
    if (this.reasonTimer) {
      clearTimeout(this.reasonTimer);
      this.reasonTimer = undefined;
    }
  }

  private emitChange(): void {
    const current = this.metadata || [];
    const existingMarker = current.find(m => m.key === BOTTLENECK_KEY);
    const existingReason = current.find(m => m.key === BOTTLENECK_REASON_KEY);
    const rest = current.filter(m => m.key !== BOTTLENECK_KEY && m.key !== BOTTLENECK_REASON_KEY);

    if (!this.isMarked) {
      if (!existingMarker && !existingReason) {
        return;
      }
      this.metadata = rest;
      this.metadataChange.emit(rest);
      return;
    }

    const next: MetaData[] = [
      ...rest,
      {
        ...(existingMarker || {}),
        key: BOTTLENECK_KEY,
        value: this.severity,
        type: 'system',
        order: rest.length,
        editable: true,
      } as MetaData,
    ];
    const trimmedReason = this.reason?.trim();
    if (trimmedReason) {
      next.push({
        ...(existingReason || {}),
        key: BOTTLENECK_REASON_KEY,
        value: trimmedReason,
        type: 'system',
        order: rest.length + 1,
        editable: true,
      } as MetaData);
    }
    this.metadata = next;
    this.metadataChange.emit(next);
  }
}
